// Link Files Modal - Simple manual grouping
import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Link as LinkIcon, Check } from 'lucide-react';
import { AudioFile } from '@types';
import { useFileStore } from '@stores/useFileStore';
import { useToast } from '@components/ui';
import { cn } from '@utils/classNames';
import { groupFilesByName } from '@utils/fileGrouping';
import { showSuspensionToastIfNeeded, SUSPENSION_ERROR_MESSAGE } from '@utils/suspension';

interface LinkFilesModalProps {
  file: AudioFile;
  onClose: () => void;
  onSuccess: () => void;
}

export const LinkFilesModal: React.FC<LinkFilesModalProps> = ({ file, onClose, onSuccess }) => {
  const { files, updateFile } = useFileStore();
  const { success, error: showError } = useToast();
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // Get all MAIN files except the current one
  // Use the same grouping logic as CloudExplorer
  const fileGroups = useMemo(() => {
    return groupFilesByName(files);
  }, [files]);

  // Get only main files (exclude current file and its versions)
  const availableFiles = fileGroups
    .map(g => g.mainFile)
    .filter(f => f.id !== file.id && (!file.groupId || f.groupId !== file.groupId));

  // Filter by search query
  const filteredFiles = searchQuery
    ? availableFiles.filter(f => 
        f.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : availableFiles;

  const toggleFile = (fileId: string) => {
    const newSelected = new Set(selectedFiles);
    if (newSelected.has(fileId)) {
      newSelected.delete(fileId);
    } else {
      newSelected.add(fileId);
    }
    setSelectedFiles(newSelected);
  };

  const handleLink = async () => {
    // Check suspension FIRST before any operations
    const isSuspended = await showSuspensionToastIfNeeded(showError);
    if (isSuspended) {
      console.log('🚫 [LinkFilesModal] User is suspended - blocking file linking');
      return;
    }
    
    try {
      // Generate a new groupId if the current file doesn't have one
      const groupId = file.groupId || `group-${Date.now()}`;
      
      console.log('🔗 Starting link process:', {
        mainFile: file.name,
        mainFileId: file.id,
        currentGroupId: file.groupId,
        newGroupId: groupId,
        selectedFileIds: Array.from(selectedFiles),
        linkedFiles: Array.from(selectedFiles).map(id => {
          const f = files.find(f => f.id === id);
          return { id, name: f?.name, currentGroupId: f?.groupId };
        })
      });

      // Collect all files that need to be updated
      const filesToUpdate = new Set<string>();
      
      // Add the main file
      filesToUpdate.add(file.id);
      
      // Add all selected files
      selectedFiles.forEach(id => filesToUpdate.add(id));
      
      // IMPORTANT: If a selected file is already in another group, 
      // move ALL files from that group to the new group
      for (const fileId of selectedFiles) {
        const selectedFile = files.find(f => f.id === fileId);
        if (selectedFile?.groupId && selectedFile.groupId !== groupId) {
          console.log(`⚠️ File "${selectedFile.name}" is already in group "${selectedFile.groupId}"`);
          console.log(`🔄 Moving all files from "${selectedFile.groupId}" to "${groupId}"`);
          
          // Find all files in the old group and add them to the update list
          files.forEach(f => {
            if (f.groupId === selectedFile.groupId) {
              console.log(`   → Moving "${f.name}" to new group`);
              filesToUpdate.add(f.id);
            }
          });
        }
      }

      console.log(`🔄 Updating ${filesToUpdate.size} file(s) with groupId: ${groupId}`);

      // Update all files
      for (const fileId of filesToUpdate) {
        const fileName = files.find(f => f.id === fileId)?.name || fileId;
        console.log(`🔄 Updating "${fileName}" with groupId: ${groupId}`);
        await updateFile(fileId, { groupId });
      }

      console.log('✅ All files linked successfully');
      success('Files linked', `${filesToUpdate.size} file(s) linked successfully`);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('❌ Failed to link files:', err);
      if (err.message === 'ACCOUNT_SUSPENDED') {
        showError(SUSPENSION_ERROR_MESSAGE.title, SUSPENSION_ERROR_MESSAGE.message);
      } else {
        showError('Failed to link files', 'Please try again.');
      }
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl bg-dark-secondary border border-white/10 rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b border-white/10">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <LinkIcon className="w-4 h-4 text-primary flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h2 className="font-dela text-base text-white truncate">Link Files</h2>
              <p className="text-xs text-white/60 truncate">Group "{file.name}" with other files</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0 ml-2"
          >
            <X className="w-4 h-4 text-white/60" />
          </button>
        </div>

        {/* Search */}
        <div className="p-3 border-b border-white/10">
          <input
            type="text"
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-1.5 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-primary"
          />
        </div>

        {/* File List */}
        <div className="max-h-80 overflow-y-auto p-3">
          {filteredFiles.length === 0 ? (
            <div className="text-center py-8 text-white/40">
              <LinkIcon className="w-12 h-12 mx-auto mb-2 opacity-40" />
              <p>No files found</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {filteredFiles.map((availableFile) => {
                const isSelected = selectedFiles.has(availableFile.id);
                const isAlreadyLinked = Boolean(availableFile.groupId === file.groupId && file.groupId);

                return (
                  <button
                    key={availableFile.id}
                    onClick={() => !isAlreadyLinked && toggleFile(availableFile.id)}
                    disabled={isAlreadyLinked}
                    className={cn(
                      'w-full p-2 rounded-lg border text-left transition-all',
                      isAlreadyLinked && 'opacity-50 cursor-not-allowed',
                      isSelected && !isAlreadyLinked && 'bg-primary/20 border-primary',
                      !isSelected && !isAlreadyLinked && 'bg-white/5 border-white/10 hover:bg-white/10'
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-inter font-semibold text-sm text-white truncate block">
                            {availableFile.name}
                          </span>
                          {isAlreadyLinked && (
                            <span className="px-1.5 py-0.5 bg-green-500/20 text-green-400 rounded text-xs font-bold flex-shrink-0">
                              Linked
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-xs text-white/50 truncate">
                            {availableFile.type}
                          </span>
                          {availableFile.versionType && (
                            <span className="text-xs text-white/50 flex-shrink-0">
                              • {availableFile.versionType}
                            </span>
                          )}
                        </div>
                      </div>
                      {isSelected && !isAlreadyLinked && (
                        <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-3 border-t border-white/10 bg-white/5 gap-2">
          <div className="text-xs text-white/60 truncate">
            {selectedFiles.size} file(s) selected
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm bg-white/10 hover:bg-white/20 text-white rounded-lg font-inter font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleLink}
              disabled={selectedFiles.size === 0}
              className={cn(
                'px-3 py-1.5 text-sm rounded-lg font-inter font-semibold transition-colors',
                selectedFiles.size > 0
                  ? 'bg-primary hover:bg-primary-dark text-black'
                  : 'bg-white/10 text-white/40 cursor-not-allowed'
              )}
            >
              Link Files
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

